import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint, requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';

/**
 * Inlined paging helpers (single file = no extra import-map module that can fail to load / 404).
 * @typedef {{ pageSizeLoadMore: number, loadMoreLimit: number, totalItems: number, lastLogicalPage: number }} PagingConfig
 */
const PAGE_SIZE_AFTER_LIMIT = 40;

/** @param {HTMLElement | null | undefined} grid @returns {PagingConfig} */
function readPagingConfigFromGrid(grid) {
  let pageSize = 20;
  let loadMoreLimit = 0;
  let totalItems = 0;
  let lastLogicalPage = 1;
  if (grid instanceof HTMLElement) {
    pageSize = Number(grid.getAttribute('data-page-size')) || 20;
    if (pageSize < 1) pageSize = 20;
    loadMoreLimit = Math.max(0, Number(grid.getAttribute('data-load-more-page-limit')) || 0);
    totalItems = Math.max(0, Number(grid.getAttribute('total-items')) || 0);
    lastLogicalPage = Math.max(1, Number(grid.getAttribute('last-page')) || 1);
  }
  return { pageSizeLoadMore: pageSize, loadMoreLimit, totalItems, lastLogicalPage };
}

/** @param {number} logicalPage @param {number} totalItems @param {PagingConfig} cfg */
function getRangeForLogicalPage(logicalPage, totalItems, cfg) {
  const lp = Math.max(1, Number(logicalPage) || 1);
  const ti = Math.max(0, Number(totalItems) || 0);
  const s = cfg.pageSizeLoadMore;
  const limit = cfg.loadMoreLimit;
  const big = PAGE_SIZE_AFTER_LIMIT;
  let start;
  let end;
  if (limit <= 0 || lp <= limit) {
    start = (lp - 1) * s + 1;
    end = Math.min(lp * s, ti);
  } else {
    start = s * limit + (lp - limit - 1) * big + 1;
    end = Math.min(start + big - 1, ti);
  }
  return { start, end };
}

/** @param {URLSearchParams} searchParams @param {PagingConfig} cfg */
function logicalPageFromSearchParams(searchParams, cfg) {
  const view = searchParams.get('view');
  const raw = parseInt(searchParams.get('page') || '1', 10);
  const p = Number.isFinite(raw) && raw >= 1 ? raw : 1;
  if (cfg.loadMoreLimit > 0 && view === 'pagination') {
    const shopifyPagesCovered = Math.floor(cfg.loadMoreLimit * cfg.pageSizeLoadMore / PAGE_SIZE_AFTER_LIMIT);
    return cfg.loadMoreLimit + p - shopifyPagesCovered;
  }
  return p;
}

/** @param {number} logicalPage @param {string} baseHref @param {PagingConfig} cfg */
function buildCollectionPageUrl(logicalPage, baseHref, cfg) {
  try {
    const base =
      typeof baseHref === 'string' && baseHref.length > 0 ? baseHref : window.location.href;
    const url = new URL(base);
    url.searchParams.delete('_su_rec');
    url.searchParams.delete('_su_rec_id');
    const limit = cfg.loadMoreLimit;
    if (logicalPage <= 1) {
      url.searchParams.delete('page');
      url.searchParams.delete('view');
      url.searchParams.delete('lmps');
    } else if (limit > 0 && logicalPage >= limit + 1) {
      const shopifyPagesCovered = Math.floor(limit * cfg.pageSizeLoadMore / PAGE_SIZE_AFTER_LIMIT);
      const shopifyPage = logicalPage - limit + shopifyPagesCovered;
      url.searchParams.set('page', String(shopifyPage));
      url.searchParams.set('view', 'pagination');
      url.searchParams.set('lmps', String(cfg.pageSizeLoadMore));
    } else {
      url.searchParams.set('page', String(logicalPage));
      url.searchParams.delete('view');
      url.searchParams.delete('lmps');
    }
    url.hash = '';
    return url.toString();
  } catch {
    return typeof baseHref === 'string' && baseHref.length > 0 ? baseHref : window.location.href;
  }
}

/** @param {number} logicalPage @param {string} baseHref @param {PagingConfig} cfg */
function buildSectionFetchUrl(logicalPage, baseHref, cfg) {
  try {
    return new URL(buildCollectionPageUrl(logicalPage, baseHref, cfg));
  } catch {
    const u = new URL(window.location.href);
    u.searchParams.set('page', String(Math.max(1, logicalPage)));
    u.hash = '';
    return u;
  }
}

/** @param {HTMLElement} grid */
function getFurthestLogicalPageFromGrid(grid) {
  if (!(grid instanceof HTMLElement) || grid.children.length === 0) return 1;
  let max = 1;
  for (let i = 0; i < grid.children.length; i++) {
    const el = grid.children[i];
    const p = parseInt(el.getAttribute('data-page') || '1', 10);
    if (Number.isFinite(p) && p > max) max = p;
  }
  return max;
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [loadMoreWrapper] - Load more wrapper (hidden when switching to pagination).
 * @property {HTMLElement[]} [loadMoreContent] - Load more container.
 * @property {HTMLElement[]} [loadMore] - Load more button.
 * @property {HTMLElement} [loadMoreBar] - Load more progress bar.
 * @property {HTMLElement} [loadMoreCount] - Load more count element.
 * @property {HTMLElement} [paginationWrapper] - Pagination nav (shown after load more page limit).
 * @property {HTMLElement} [paginationSummary] - "Showing X to Y of Z" summary above pagination.
 * @property {HTMLElement} [paginationRange] - The text element for the range message.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** Theme editor: Horizon helper for unsaved image-ratio settings on newly loaded cards */
  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {'button' | 'infinite'} */
  #loadMode = 'infinite';

  /** @type {string | null} - Minimal section id for page 2+ (smaller payload) */
  #loadMoreSectionId = null;

  /** Prevents overlapping Load more work (double-click / slow network). */
  #loadMoreButtonBusy = false;

  /** BFCache / mobile back-forward: re-sync range + chrome from DOM + centralized paging rules. */
  #onPageShow = (event) => {
    if (event.persisted) {
      // If the grid was cleared when this page entered BFCache (see #onPaginationPageHide),
      // reload to get fresh products rather than showing an empty grid.
      const grid = this.refs?.grid;
      if (grid instanceof HTMLElement && grid.childElementCount === 0) {
        window.location.reload();
        return;
      }
      requestAnimationFrame(() => this.#syncPagingUiFromDom());
    }
  };

  /**
   * On view=pagination pages, clear the product grid when Safari stores this page in BFCache
   * before navigating to the next pagination page. Two 40-product pages in memory simultaneously
   * (one in BFCache, one loading) exceeds mobile Safari's memory budget and causes a crash.
   * If the user hits back and gets the cleared BFCache page, #onPageShow forces a reload.
   */
  #onPaginationPageHide = (event) => {
    if (!event.persisted) return;
    const { grid } = this.refs;
    if (grid instanceof HTMLElement) {
      grid.querySelectorAll('img').forEach(img => {
        img.src = '';
        img.srcset = '';
      });
      grid.innerHTML = '';
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const loadMoreEl = document.querySelector('[data-load-more-section-id]');
    this.#loadMoreSectionId = loadMoreEl?.getAttribute('data-load-more-section-id') ?? null;

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      const templateCard = this.querySelector('[ref="cardGallery"]');
      if (templateCard instanceof HTMLElement) {
        this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
      }
    }

    this.#storeImageRatioSettings();
    this.#determineLoadMode();
    this.#initializePagination();
    this.#setupPaginationLinkInterception();
    this.#revealTopSummaryIfOnPaginationPage();

    window.addEventListener('pageshow', this.#onPageShow);
    if (new URLSearchParams(window.location.search).get('view') === 'pagination') {
      window.addEventListener('pagehide', this.#onPaginationPageHide);
    }
    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Single place to refresh load-more bar, pagination chrome, and in-tree range slots from grid + config.
   */
  #syncPagingUiFromDom() {
    try {
      const grid = this.refs?.grid;
      if (!(grid instanceof HTMLElement)) return;
      const cfg = readPagingConfigFromGrid(grid);
      const logicalPage = getFurthestLogicalPageFromGrid(grid);
      this.applyPagingChrome(logicalPage, grid.childElementCount || 0, cfg);
    } catch (e) {
      console.warn('[PaginatedList] syncPagingUiFromDom failed', e);
    }
  }

  /**
   * On full page load, fix facet range / chrome when URL state is ahead of default SSR (?page=, view=pagination).
   */
  #revealTopSummaryIfOnPaginationPage() {
    requestAnimationFrame(() => {
      try {
        const grid = this.refs?.grid;
        if (!(grid instanceof HTMLElement)) return;
        const cfg = readPagingConfigFromGrid(grid);
        const fromUrl = logicalPageFromSearchParams(new URLSearchParams(window.location.search), cfg);
        const fromDom = getFurthestLogicalPageFromGrid(grid);
        const logicalPage = Math.max(fromUrl, fromDom);
        if (logicalPage < 1) return;
        this.applyPagingChrome(logicalPage, grid.childElementCount || 0, cfg);
      } catch (e) {
        console.warn('[PaginatedList] revealTopSummary failed', e);
      }
    });
  }

  /**
   * Get the load more button element (ref may be single or array)
   * @returns {HTMLElement | undefined}
   */
  #getLoadMoreButton() {
    const { loadMore } = this.refs;
    if (loadMore instanceof HTMLElement) return loadMore;
    if (Array.isArray(loadMore) && loadMore[0] instanceof HTMLElement) return loadMore[0];
    return undefined;
  }

  /**
   * Determine the loading mode based on available elements
   */
  #determineLoadMode() {
    this.#loadMode = this.#getLoadMoreButton() ? 'button' : 'infinite';
  }

  /**
   * Initialize pagination based on determined mode
   */
  #initializePagination() {
    // Infinite scroll: warm next/previous in the background. Load-more *button*: skip prefetch —
    // holding a full section HTML string + growing product DOM peaks memory and crashes WebKit
    // ("A problem repeatedly occurred") after a few clicks on heavy collections.
    if (this.#loadMode === 'infinite') {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }

    if (this.#loadMode === 'button') {
      this.#initializeButtonMode();
    } else {
      this.#observeViewMore();
    }
  }

  /**
   * Initialize button-based loading mode
   */
  #initializeButtonMode() {
    const loadMore = this.#getLoadMoreButton();
    if (!loadMore) return;

    // Wait for the DOM to be fully rendered before attaching the event listener
    requestAnimationFrame(() => {
      loadMore.addEventListener('click', this.#handleLoadMoreClick);
    });
  }

  /**
   * Intercept pagination link clicks so we fetch the section with the link URL (view=pagination),
   * ensuring the server returns 40 per page from collection.pagination template.
   */
  #setupPaginationLinkInterception() {
    this.addEventListener('click', this.#handlePaginationLinkClick);
  }

  /**
   * Pagination links: use logical page (data-page) when present so URL is built correctly for 20/40 per page.
   */
  #handlePaginationLinkClick = (e) => {
    const link = e.target?.closest?.('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const nav = link.closest('.load-more-pagination');
    if (!nav) return;
    if (link.hasAttribute('aria-current')) return;

    const href = link.getAttribute('href') || link.href;
    if (!href || href === '#') return;

    const li = link.closest('[data-page]');
    let logicalPage = li ? parseInt(li.getAttribute('data-page'), 10) : NaN;

    if (!Number.isFinite(logicalPage) || logicalPage < 1) {
      const currentLi = nav.querySelector('.pagination__link--current')?.closest('[data-page]');
      const currentPage = currentLi ? parseInt(currentLi.getAttribute('data-page'), 10) : 1;

      if (link.hasAttribute('data-pagination-prev') || link.closest('[data-pagination-prev]')) {
        logicalPage = Math.max(currentPage - 1, 1);
      } else if (link.hasAttribute('data-pagination-next') || link.closest('[data-pagination-next]')) {
        const lastPage = Number(this.refs?.grid?.getAttribute('last-page')) || 1;
        logicalPage = Math.min(currentPage + 1, lastPage);
      } else {
        const url = new URL(href, window.location.origin);
        logicalPage = parseInt(url.searchParams.get('page') || '1', 10);
        if (!Number.isFinite(logicalPage) || logicalPage < 1) return;
      }
    }

    const grid = this.refs?.grid;
    const cfg = readPagingConfigFromGrid(grid instanceof HTMLElement ? grid : null);
    const targetUrl = buildCollectionPageUrl(logicalPage, window.location.href, cfg);
    e.preventDefault();
    window.location.assign(targetUrl);
  };

  /**
   * Set up (or re-set up) IntersectionObserver on whichever sentinel refs exist.
   * Tolerant: works with one or both sentinels (aligned with Horizon's #observeViewMore).
   */
  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                try {
                  await this.#renderPreviousPage();
                } catch (e) {
                  console.warn('[PaginatedList] renderPreviousPage failed', e);
                }
              } else if (entry.target === viewMoreNext) {
                try {
                  await this.#renderNextPage();
                } catch (e) {
                  console.warn('[PaginatedList] renderNextPage failed', e);
                }
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Handle load more button click.
   * @param {Event} [event] - Click event.
   */
  #handleLoadMoreClick = async (_event) => {
    if (this.#loadMoreButtonBusy) return;

    const loadMoreBtn = this.#getLoadMoreButton();
    this.#loadMoreButtonBusy = true;
    if (loadMoreBtn instanceof HTMLButtonElement) loadMoreBtn.disabled = true;

    const releaseButton = () => {
      this.#loadMoreButtonBusy = false;
      if (loadMoreBtn instanceof HTMLButtonElement) loadMoreBtn.disabled = false;
    };

    try {
      const result = await this.#renderNextPage();
      if (!result) {
        releaseButton();
        return;
      }

      const finish = () => {
        try {
          this.updateLoadMoreBlock(result.page, result.itemCount);
          if (this.#loadMode === 'infinite') {
            this.#fetchPage('next');
          }
        } finally {
          releaseButton();
        }
      };

      if (result.deferred) {
        const deferMs = PaginatedList.#DEFER_FIRST_CHUNK_IDLE_MS;
        const runDeferred = () => {
          this.#runDeferredAppend(result, finish);
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(runDeferred, { timeout: deferMs });
        } else {
          setTimeout(runDeferred, deferMs);
        }
      } else {
        requestAnimationFrame(() => finish());
      }
    } catch (error) {
      console.warn('[PaginatedList] Load more failed', error);
      releaseButton();
    }
  };

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.removeEventListener('click', this.#handlePaginationLinkClick);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPaginationPageHide);

    const loadMore = this.#getLoadMoreButton();
    if (loadMore) {
      loadMore.removeEventListener('click', this.#handleLoadMoreClick);
    }
  }

  /**
   * DOM region for paging copy (facets + mobile bar): stay inside the listing, never document-wide.
   * @returns {HTMLElement}
   */
  #pagingChromeRoot() {
    return this.querySelector('.collection-wrapper') ?? this;
  }

  /**
   * Update only `[data-pagination-range-top]` nodes under the listing region.
   * @param {string} rangeText
   */
  #updatePaginationRangeTopSlots(rangeText) {
    if (!rangeText) return;
    const root = this.#pagingChromeRoot();
    root.querySelectorAll('[data-pagination-range-top]').forEach((el) => {
      if (el instanceof HTMLElement) el.textContent = rangeText;
    });
  }

  /**
   * State-driven UI: load-more bar, optional pagination block, range text (scoped regions only).
   * @param {number} logicalPage - Current furthest logical page (1-based)
   * @param {number} [knownItemCount] - Grid item count when known (e.g. after append)
   * @param {PagingConfig | null} [cfgOverride] - Optional cached config from grid
   */
  applyPagingChrome = (logicalPage, knownItemCount, cfgOverride = null) => {
    try {
      this.#applyPagingChromeInner(logicalPage, knownItemCount, cfgOverride);
    } catch (e) {
      console.warn('[PaginatedList] applyPagingChrome failed', e);
    }
  };

  /** @param {number} logicalPage @param {number} [knownItemCount] @param {PagingConfig | null} [cfgOverride] */
  #applyPagingChromeInner = (logicalPage, knownItemCount, cfgOverride = null) => {
    const { loadMoreContent, loadMoreWrapper, grid, loadMoreBar, loadMoreCount, paginationWrapper } = this.refs;
    const loadMoreBtn = this.#getLoadMoreButton();

    if (!grid) return;

    const cfg = cfgOverride ?? readPagingConfigFromGrid(grid);
    const gridItemsCount = knownItemCount !== undefined ? knownItemCount : grid.childElementCount || 0;
    const totalPages = cfg.lastLogicalPage;
    const totalItems = cfg.totalItems;
    const loadMorePageLimit = cfg.loadMoreLimit;
    const percent = totalItems > 0 ? (gridItemsCount / totalItems) * 100 : 0;

    const hasLoadMoreChrome =
      loadMoreContent instanceof HTMLElement &&
      loadMoreBtn instanceof HTMLElement &&
      loadMoreBar instanceof HTMLElement;

    if (hasLoadMoreChrome) {
      const countSlot =
        (loadMoreCount instanceof HTMLElement ? loadMoreCount : null) ??
        loadMoreContent.querySelector('[ref="loadMoreCount"]');
      if (countSlot) countSlot.textContent = String(gridItemsCount);
      loadMoreBar.style.setProperty('--active-width', `${percent}%`);
    }

    const { start, end } = getRangeForLogicalPage(logicalPage, totalItems, cfg);
    const rangeText = totalItems > 0 ? `Showing ${start} to ${end} of ${totalItems}` : '';
    this.#updatePaginationRangeTopSlots(rangeText);

    const atOrPastLoadMoreLimit = loadMorePageLimit > 0 && logicalPage >= loadMorePageLimit;
    if (atOrPastLoadMoreLimit && paginationWrapper instanceof HTMLElement) {
      paginationWrapper.classList.remove('hidden');
      this.#syncPaginationToPage(logicalPage, totalPages, cfg);
      const paginationSummary = this.refs.paginationSummary;
      const paginationRange = this.refs.paginationRange;
      if (paginationSummary instanceof HTMLElement) paginationSummary.classList.remove('hidden');
      if (paginationRange) paginationRange.textContent = rangeText;
    }
    if (atOrPastLoadMoreLimit && loadMoreWrapper instanceof HTMLElement) {
      loadMoreWrapper.classList.add('hidden');
    }
    if (hasLoadMoreChrome && !atOrPastLoadMoreLimit && logicalPage >= totalPages) {
      loadMoreBtn.classList.add('hidden');
    }
  };

  /** @deprecated Use applyPagingChrome; kept for call-site stability */
  updateLoadMoreBlock = (currentPage, knownItemCount) => this.applyPagingChrome(currentPage, knownItemCount);

  /** Inline SVG for pagination prev icon (chevron left) */
  static #PAGINATION_PREV_ICON =
    '<span class="pagination__icon pagination__icon--prev" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="20" height="20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12.5 15l-5-5 5-5"/></svg></span>';
  /** Inline SVG for pagination next icon (chevron right) */
  static #PAGINATION_NEXT_ICON =
    '<span class="pagination__icon pagination__icon--next" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="20" height="20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 5l5 5-5 5"/></svg></span>';

  /**
   * Sync pagination nav to the current page (e.g. after Load more twice, current page is 3).
   * Updates which item is "current", Prev/Next (show prev only when currentPage > 1), and hrefs.
   * @param {number} currentPage - The logical current page (1-based)
   * @param {number} totalPages - Total number of pages
   * @param {PagingConfig} cfg
   */
  #syncPaginationToPage(currentPage, totalPages, cfg) {
    const { paginationWrapper } = this.refs;
    if (!(paginationWrapper instanceof HTMLElement)) return;

    const baseHref = window.location.href;
    const pageUrl = (/** @type {number} */ p) => buildCollectionPageUrl(p, baseHref, cfg);

    const list = paginationWrapper.querySelector('.pagination__list');
    if (!(list instanceof HTMLElement)) return;

    const pageLabel = paginationWrapper.getAttribute('aria-label') || 'Page';

    const numbersContainer = list.querySelector('.pagination__numbers');
    if (numbersContainer && totalPages > 0) {
      const existingItems = numbersContainer.querySelectorAll('[data-page]');
      if (existingItems.length !== totalPages) {
        numbersContainer.innerHTML = '';
        for (let p = 1; p <= totalPages; p++) {
          const li = document.createElement('li');
          li.className = 'pagination__item';
          li.setAttribute('data-page', String(p));
          if (p === currentPage) {
            const span = document.createElement('span');
            span.className = 'pagination__link pagination__link--current';
            span.setAttribute('aria-current', 'page');
            span.textContent = String(p);
            li.appendChild(span);
          } else {
            const a = document.createElement('a');
            a.href = pageUrl(p);
            a.className = 'pagination__link';
            a.setAttribute('aria-label', `${pageLabel} ${p}`);
            a.textContent = String(p);
            li.appendChild(a);
          }
          numbersContainer.appendChild(li);
        }
      }
    }

    let prevItem = paginationWrapper.querySelector('.pagination__item--prev');
    let nextItem = paginationWrapper.querySelector('.pagination__item--next');

    if (currentPage > 1) {
      if (!prevItem) {
        prevItem = document.createElement('li');
        prevItem.className = 'pagination__item pagination__item--prev';
        const a = document.createElement('a');
        a.href = pageUrl(currentPage - 1);
        a.className = 'pagination__link pagination__link--icon pagination__link--prev';
        a.setAttribute('data-pagination-prev', '');
        a.setAttribute('aria-label', 'Previous');
        a.innerHTML = PaginatedList.#PAGINATION_PREV_ICON;
        prevItem.appendChild(a);
        list.insertBefore(prevItem, list.firstChild);
      } else {
        const prevLink = prevItem.querySelector('[data-pagination-prev]');
        if (prevLink instanceof HTMLAnchorElement) prevLink.href = pageUrl(currentPage - 1);
      }
    } else if (prevItem) {
      prevItem.remove();
    }

    if (currentPage < totalPages) {
      if (!nextItem) {
        nextItem = document.createElement('li');
        nextItem.className = 'pagination__item pagination__item--next';
        const a = document.createElement('a');
        a.href = pageUrl(currentPage + 1);
        a.className = 'pagination__link pagination__link--icon pagination__link--next';
        a.setAttribute('data-pagination-next', '');
        a.setAttribute('aria-label', 'Next');
        a.innerHTML = PaginatedList.#PAGINATION_NEXT_ICON;
        nextItem.appendChild(a);
        list.appendChild(nextItem);
      } else {
        const nextLink = nextItem.querySelector('[data-pagination-next]');
        if (nextLink instanceof HTMLAnchorElement) nextLink.href = pageUrl(currentPage + 1);
      }
    } else if (nextItem) {
      nextItem.remove();
    }

    const items = paginationWrapper.querySelectorAll('[data-page]');
    for (const li of items) {
      const pageNum = li.getAttribute('data-page');
      if (pageNum == null) continue;
      const page = Number(pageNum);
      const isCurrent = page === currentPage;
      const linkOrSpan = li.querySelector('.pagination__link');
      if (!(linkOrSpan instanceof HTMLElement)) continue;

      if (isCurrent) {
        if (linkOrSpan instanceof HTMLAnchorElement) {
          const span = document.createElement('span');
          span.className = 'pagination__link pagination__link--current';
          span.setAttribute('aria-current', 'page');
          span.textContent = linkOrSpan.textContent;
          linkOrSpan.replaceWith(span);
        } else {
          linkOrSpan.classList.add('pagination__link--current');
          linkOrSpan.setAttribute('aria-current', 'page');
        }
      } else {
        if (linkOrSpan instanceof HTMLSpanElement) {
          const a = document.createElement('a');
          a.href = pageUrl(page);
          a.className = 'pagination__link';
          a.setAttribute('aria-label', `${pageLabel} ${page}`);
          a.textContent = linkOrSpan.textContent;
          linkOrSpan.replaceWith(a);
        } else if (linkOrSpan instanceof HTMLAnchorElement) {
          linkOrSpan.href = pageUrl(page);
          linkOrSpan.classList.remove('pagination__link--current');
          linkOrSpan.removeAttribute('aria-current');
        }
      }
    }
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.warn('[PaginatedList] Prefetch failed', type, error);
    } finally {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId || !sectionId.trim()) {
      console.warn('[PaginatedList] Missing section-id; skip section fetch');
      return;
    }

    const grid = this.refs?.grid;
    const cfg = readPagingConfigFromGrid(grid instanceof HTMLElement ? grid : null);
    const pageInfo = {
      page: pageNumber,
      url: url ?? buildSectionFetchUrl(pageNumber, window.location.href, cfg),
    };

    if (!this.#shouldUsePage(pageInfo)) return;
    // Use minimal load-more section for page 2+ to reduce payload and memory (theme-only fix)
    const sectionIdToUse =
      pageNumber >= 2 && this.#loadMoreSectionId ? this.#loadMoreSectionId : sectionId;
    const pageContent = await sectionRenderer.getSectionHTML(
      sectionIdToUse,
      false,
      pageInfo.url
    );
    if (!this.#sectionHtmlHasProductGrid(pageContent)) {
      console.warn('[PaginatedList] Section response missing product grid for page', pageNumber);
      return;
    }
    // Keep at most one prefetched page HTML (multi-MB strings add up fast on WebKit).
    for (const key of [...this.pages.keys()]) {
      if (key !== pageNumber) this.pages.delete(key);
    }
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {string | undefined} html
   * @returns {boolean}
   */
  #sectionHtmlHasProductGrid(html) {
    if (typeof html !== 'string' || html.length === 0) return false;
    // Avoid a second full DOMParser tree here (fetch path already allocates the string; WebKit is tight on RAM).
    const g = html.includes('ref="grid"') || html.includes("ref='grid'");
    const c = html.includes('cards[]');
    return g && c;
  }

  /** Chunk size for append when grid is still small (desktop) */
  static #APPEND_CHUNK_SIZE = 14;
  /** Smaller chunk when grid is already large (desktop) */
  static #APPEND_CHUNK_SIZE_LARGE = 6;
  /** Grid size above which we use the smaller chunk (desktop) */
  static #LARGE_GRID_THRESHOLD = 24;
  /** Mobile: smaller chunks and lower threshold for better INP on slower devices */
  static #APPEND_CHUNK_SIZE_MOBILE = 4;
  static #APPEND_CHUNK_SIZE_LARGE_MOBILE = 4;
  static #LARGE_GRID_THRESHOLD_MOBILE = 12;
  /** Defer first chunk to idle so click handler doesn't trigger layout (better INP) */
  static #DEFER_FIRST_CHUNK_IDLE_MS = 32;

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return undefined;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return undefined;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });
      this.#fetchPage('next');
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return undefined;
    }

    const items = Array.from(nextPageItemElements);
    // Drop stored HTML immediately; nodes stay alive until appended (reduces peak RSS on macOS/iOS WebKit).
    this.pages.delete(nextPage.page);

    const countBefore = grid.childElementCount;
    const itemCount = countBefore + items.length;
    const isMobile = isMobileBreakpoint();
    const largeThreshold = isMobile ? PaginatedList.#LARGE_GRID_THRESHOLD_MOBILE : PaginatedList.#LARGE_GRID_THRESHOLD;
    const chunkLarge = isMobile ? PaginatedList.#APPEND_CHUNK_SIZE_LARGE_MOBILE : PaginatedList.#APPEND_CHUNK_SIZE_LARGE;
    const chunkDefault = isMobile ? PaginatedList.#APPEND_CHUNK_SIZE_MOBILE : PaginatedList.#APPEND_CHUNK_SIZE;
    const chunkSize = countBefore >= largeThreshold ? chunkLarge : chunkDefault;

    if (items.length <= chunkSize) {
      grid.append(...items);
      this.#processNewElementsInElements(items);
      if (this.#loadMode === 'infinite') {
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      }
      return { page: nextPage.page, itemCount };
    }

    // Infinite scroll: run chunked append inline because no caller handles the
    // deferred result (the IntersectionObserver discards the return value).
    if (this.#loadMode === 'infinite') {
      grid.append(...items.slice(0, chunkSize));
      await new Promise((resolve) => {
        this.#appendRemainingChunks(grid, items, chunkSize, nextPage.page, resolve);
      });
      requestIdleCallback(() => this.#fetchPage('next'));
      return { page: nextPage.page, itemCount };
    }

    // Button mode: return deferred for handleLoadMoreClick to process
    return {
      page: nextPage.page,
      itemCount,
      deferred: { grid, items, chunkSize, page: nextPage.page },
    };
  }

  /**
   * Run the first chunk append (and rest) when result has deferred data.
   * Called from handleLoadMoreClick so we don't block the click frame with layout.
   * @param {{ deferred?: { grid: HTMLElement; items: Element[]; chunkSize: number; page?: number } }} result
   * @param {() => void} [onComplete] - Called after every chunk is appended (e.g. re-enable button, update chrome).
   */
  #runDeferredAppend(result, onComplete) {
    const deferred = result.deferred;
    if (!deferred) {
      onComplete?.();
      return;
    }
    const { grid, items, chunkSize, page } = deferred;
    const firstChunk = items.slice(0, chunkSize);
    grid.append(...firstChunk);
    this.#appendRemainingChunks(grid, items, chunkSize, page, onComplete);
  }

  /**
   * Append remaining items in chunks per frame to avoid one heavy layout (improves INP).
   * Drops cached page HTML when done to limit memory growth when loading many pages.
   * @param {HTMLElement} grid
   * @param {Element[]} items
   * @param {number} chunkSize
   * @param {number} [pageToDrop] - Page number to remove from cache when append is done
   * @param {() => void} [onComplete] - Called when all chunks are on the DOM
   */
  #appendRemainingChunks(grid, items, chunkSize, pageToDrop, onComplete) {
    let start = chunkSize;
    const run = () => {
      if (start >= items.length) {
        this.#processNewElementsInElements(items);
        if (pageToDrop !== undefined) this.pages.delete(pageToDrop);
        onComplete?.();
        return;
      }
      const end = Math.min(start + chunkSize, items.length);
      grid.append(...items.slice(start, end));
      start = end;
      requestAnimationFrame(run);
    };
    requestAnimationFrame(run);
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      this.#fetchPage('previous');
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();
    this.pages.delete(previousPage.page);

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      const y = scrollTop + heightDiff;
      try {
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
      } catch {
        window.scrollTo(0, y);
      }
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Process newly added elements and apply correct aspect ratios.
   * Deferred to requestIdleCallback so the first paint after append is not blocked.
   */
  #processNewElements() {
    this.#runProcessNewElements(null);
  }

  /**
   * Process only galleries inside the given elements (avoids scanning whole grid as it grows).
   * Use after chunked append so work scales with new items, not total list size.
   * @param {Element[]} cardElements - Newly appended card/row elements
   */
  #processNewElementsInElements(cardElements) {
    const galleries = cardElements.flatMap((el) =>
      Array.from(el.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])'))
    ).filter((g) => g instanceof HTMLElement);
    if (galleries.length === 0) {
      this.#runProcessNewElements(null, cardElements);
      return;
    }
    this.#runProcessNewElements(/** @type {HTMLElement[]} */(galleries), cardElements);
  }

  /**
   * @param {HTMLElement[] | null} galleriesOnly - If set, only process these galleries; else query all unprocessed.
   * @param {Element[]} [newCardElements] - Newly appended cards; passed in event so listeners only process these (avoids O(n) over whole grid).
   */
  #runProcessNewElements(galleriesOnly, newCardElements) {
    const run = () => {
      // Design mode: use Horizon helper so unsaved image-ratio theme settings apply to injected cards.
      // Storefront: keep Sabo’s scoped/batched aspect-ratio path (helper no-ops outside designMode).
      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      } else if (galleriesOnly) {
        this.#imageRatioSetting === 'adapt'
          ? this.#fixAdaptiveAspectRatiosIn(galleriesOnly)
          : this.#applyFixedAspectRatioIn(galleriesOnly);
      } else {
        this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
      }
      this.dispatchEvent(
        new CustomEvent('paginated-list:new-elements', { bubbles: false, detail: { elements: newCardElements ?? [] } })
      );
    };
    const timeout = isMobileBreakpoint() ? 300 : 200;
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout });
    } else {
      requestAnimationFrame(run);
    }
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers.
   * Batches read (querySelectorAll) before writes to avoid layout thrashing.
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
    for (let i = 0; i < mediaContainers.length; i++) {
      const container = mediaContainers[i];
      if (container instanceof HTMLElement) container.style.aspectRatio = aspectRatio;
    }
    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards.
   * Batches all DOM reads first, then all writes to avoid layout thrashing.
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;
    this.#fixAdaptiveAspectRatiosIn(Array.from(newCardGalleries));
  }

  /**
   * Same as #fixAdaptiveAspectRatios but only for the given galleries (scoped, no full-grid query).
   * @param {HTMLElement[]} newCardGalleries
   */
  async #fixAdaptiveAspectRatiosIn(newCardGalleries) {
    if (!newCardGalleries.length) return;

    const BATCH_SIZE = 15;
    const productRatioCache = new Map();
    /** @type {{ gallery: HTMLElement, aspectRatio: string, containers: HTMLElement[] }[]} */
    const toApply = [];
    /** @type {HTMLElement[]} */
    const deferred = [];

    for (let i = 0; i < newCardGalleries.length; i++) {
      const gallery = newCardGalleries[i];
      if (!(gallery instanceof HTMLElement)) continue;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        const ratio = productRatioCache.get(productId);
        const containers = Array.from(gallery.querySelectorAll('.product-media-container')).filter((c) => c instanceof HTMLElement);
        toApply.push({ gallery, aspectRatio: ratio, containers });
        continue;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        const containers = Array.from(gallery.querySelectorAll('.product-media-container')).filter((c) => c instanceof HTMLElement);
        toApply.push({ gallery, aspectRatio: '1', containers });
        continue;
      }

      if (img.complete && img.naturalWidth && img.naturalHeight) {
        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);
        if (productId) productRatioCache.set(productId, imgRatio);
        const containers = Array.from(gallery.querySelectorAll('.product-media-container')).filter((c) => c instanceof HTMLElement);
        toApply.push({ gallery, aspectRatio: imgRatio, containers });
      } else {
        deferred.push(gallery);
      }
    }

    for (let i = 0; i < toApply.length; i++) {
      const { gallery, aspectRatio, containers } = toApply[i];
      gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
      for (let j = 0; j < containers.length; j++) containers[j].style.aspectRatio = aspectRatio;
      this.#markAsProcessed(gallery);
      if (i % BATCH_SIZE === BATCH_SIZE - 1 && i < toApply.length - 1) await yieldToMainThread();
    }

    for (let i = 0; i < deferred.length; i++) {
      this.#fixAdaptiveAspectRatioDeferred(deferred[i], productRatioCache);
    }
  }

  /**
   * Apply aspect ratio for one gallery when its image loads (deferred from #fixAdaptiveAspectRatios).
   * @param {HTMLElement} gallery
   * @param {Map<string, string>} productRatioCache
   */
  #fixAdaptiveAspectRatioDeferred(gallery, productRatioCache) {
    const productId = gallery.getAttribute('data-product-id');
    const img = gallery.querySelector('img');
    if (!img) {
      this.#applyAspectRatioToGallery(gallery, '1');
      return;
    }
    const loadAndSetRatio = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);
      if (productId) productRatioCache.set(productId, imgRatio);
      this.#applyAspectRatioToGallery(gallery, imgRatio);
    };
    if (img.complete) loadAndSetRatio();
    else img.addEventListener('load', loadAndSetRatio, { once: true });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements.
   * Only used for non-adaptive modes (square, portrait, landscape).
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;
    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;
    this.#applyFixedAspectRatioIn(Array.from(newCardGalleries));
  }

  /**
   * Same as #applyFixedAspectRatio but only for the given galleries (scoped).
   * @param {HTMLElement[]} newCardGalleries
   */
  async #applyFixedAspectRatioIn(newCardGalleries) {
    if (!this.#imageRatioSetting || !newCardGalleries.length) return;
    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;
    const BATCH_SIZE = 15;
    const toApply = [];
    for (let i = 0; i < newCardGalleries.length; i++) {
      const gallery = newCardGalleries[i];
      if (!(gallery instanceof HTMLElement)) continue;
      const containers = Array.from(gallery.querySelectorAll('.product-media-container')).filter((c) => c instanceof HTMLElement);
      toApply.push({ gallery, containers });
    }
    for (let i = 0; i < toApply.length; i++) {
      const { gallery, containers } = toApply[i];
      gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);
      for (let j = 0; j < containers.length; j++) containers[j].style.aspectRatio = aspectRatio;
      this.#markAsProcessed(gallery);
      if (i % BATCH_SIZE === BATCH_SIZE - 1 && i < toApply.length - 1) await yieldToMainThread();
    }
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid, cards } = this.refs;
    const isPrevious = type === 'previous';
    const cfg = readPagingConfigFromGrid(grid instanceof HTMLElement ? grid : null);

    // Prefer grid's actual children so we're correct after appending (refs.cards may update async)
    if (grid instanceof HTMLElement && grid.children.length > 0) {
      const targetChild = isPrevious ? grid.firstElementChild : grid.lastElementChild;
      const pageAttr = targetChild?.getAttribute?.('data-page');
      if (pageAttr != null) {
        const page = isPrevious ? Number(pageAttr) - 1 : Number(pageAttr) + 1;
        if (page >= 1) {
          if (!isPrevious && page > cfg.lastLogicalPage) return undefined;
          const url = buildSectionFetchUrl(page, window.location.href, cfg);
          return { page, url };
        }
      }
    }

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;
    if (!isPrevious && page > cfg.lastLogicalPage) return undefined;

    const url = buildSectionFetchUrl(page, window.location.href, cfg);

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id')?.trim();
    if (!id) console.warn('[PaginatedList] section-id attribute is missing');
    return id || '';
  }

  /**
   * Handle filter updates by clearing cached pages.
   * For infinite scroll: re-observe sentinels once the DOM settles (Horizon pattern).
   * For button mode: prefetch page 2 (existing behaviour).
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#loadMoreButtonBusy = false;
    const loadMoreBtn = this.#getLoadMoreButton();
    if (loadMoreBtn instanceof HTMLButtonElement) loadMoreBtn.disabled = false;

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    if (this.#loadMode === 'infinite') {
      const currentLastPage = this.refs.grid?.getAttribute('last-page');

      const observer = new MutationObserver(() => {
        const newLastPage = this.refs.grid?.getAttribute('last-page');
        if (newLastPage !== currentLastPage) {
          observer.disconnect();
          if (!this.isConnected) return;
          this.#observeViewMore();
          this.#fetchPage('next');
        }
      });

      const { grid } = this.refs;
      if (grid) {
        observer.observe(grid, {
          attributes: true,
          attributeFilter: ['last-page'],
          childList: true,
        });
        setTimeout(() => observer.disconnect(), 3000);
      }
    } else {
      void this.#fetchSpecificPage(2).catch((err) => {
        console.warn('[PaginatedList] Post-filter prefetch failed', err);
      });
    }
  };
}
